import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi";
import {
  Form,
  Input,
  Select,
  InputNumber,
} from "antd";
import {
  CheckCircleOutlined,
  StopOutlined,
  DollarOutlined,
  GiftOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import APIs from "../../Constants/apis";
import ProductMultiSelectionModal from "../../components/CustomComponents/modal/productMultiSelectionModel";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import "../../components/CustomComponents/Switchery/ToggleSwitch.css";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import "./createReward.css";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";

const { Option } = Select;

const REWARD_TYPES = [
  {
    value: "DISCOUNT_ON_SALE",
    title: "Sale Discount",
    description: "Give a fixed or percentage discount on the entire order.",
    Icon: DollarOutlined,
  },
  {
    value: "FREE_ITEM",
    title: "Free Item",
    description: "Let customers redeem points for a free menu item.",
    Icon: GiftOutlined,
  },
];

const CreateReward = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [rewardType, setRewardType] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedItemLabels, setSelectedItemLabels] = useState(() => new Map());
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedFreeItem, setSelectedFreeItem] = useState(null);
  const [selectorLoading, setSelectorLoading] = useState(false);
  const [statusEnabled, setStatusEnabled] = useState(true);
  const [checkingRule, setCheckingRule] = useState(true);
  const [hasLoyaltyRule, setHasLoyaltyRule] = useState(false);

  const [modals, setModals] = useState({
    showCategoryModal: false,
    showItemModal: false,
    showFreeItemModal: false,
  });

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;

  const watchedRewardName = Form.useWatch("RewardName", form);
  const watchedDiscountType = Form.useWatch("DiscountType", form);
  const watchedAmount = Form.useWatch("Amount", form);
  const watchedRequiredPoints = Form.useWatch("RequiredPoints", form);

  useEffect(() => {
    const ensureLoyaltyRuleExists = async () => {
      if (!CompID) {
        setCheckingRule(false);
        return;
      }
      try {
        setCheckingRule(true);
        const response = await axiosInstance.get(
          `${APIs.GET_LOYALTY_RULE}?compID=${CompID}`
        );
        const isSuccess = response.data.status || response.data.Status;
        const ruleData = response.data.data || response.data.Data;
        const ruleId = ruleData?.rule_ID || ruleData?.Rule_ID;
        if (isSuccess && ruleId && ruleId > 0) {
          setHasLoyaltyRule(true);
          loadCategories();
        } else {
          setHasLoyaltyRule(false);
          showErrorToast(
            "A loyalty rule must be set up before creating rewards."
          );
          navigate("/loyalty/rules");
        }
      } catch (error) {
        console.error("Error checking loyalty rule:", error);
        showErrorToast(
          "A loyalty rule must be set up before creating rewards."
        );
        navigate("/loyalty/rules");
      } finally {
        setCheckingRule(false);
      }
    };

    ensureLoyaltyRuleExists();
  }, [CompID, navigate]);

  const loadCategories = async () => {
    try {
      const response = await axiosInstance.get(
        `${APIs.GET_CATEGORIES}?compID=${CompID}`
      );
      if (response.data.status || response.data.Status) {
        setCategories(response.data.data || response.data.Data || []);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
      setCategories([]);
    }
  };

  const openModal = (modalName) => {
    setModals((prev) => {
      const newModals = {};
      Object.keys(prev).forEach((key) => {
        newModals[key] = key === modalName;
      });
      return newModals;
    });
    document.body.style.overflow = "hidden";
    document.body.classList.add("modal-open");
  };

  const closeAllModals = () => {
    setModals((prev) => {
      const newModals = {};
      Object.keys(prev).forEach((key) => {
        newModals[key] = false;
      });
      return newModals;
    });
    document.body.style.overflow = "unset";
    document.body.classList.remove("modal-open");
  };

  const handleFreeItemSelect = (selectedProducts) => {
    const productArray = Array.isArray(selectedProducts) ? selectedProducts : [];
    const first = productArray.length > 0 ? productArray[0] : null;

    if (first) {
      const selectedItem =
        typeof first === "object"
          ? {
              ...first,
              MenuItem_ID:
                first.MenuItem_ID || first.menuItem_ID || first.id,
              MenuItem_Name:
                first.MenuItem_Name || first.menuItem_Name || first.name || "",
            }
          : {
              MenuItem_ID: first,
              menuItem_ID: first,
              MenuItem_Name: `Item ${first}`,
            };
      setSelectedFreeItem(selectedItem);
      form.setFieldsValue({ FreeItemID: selectedItem.MenuItem_ID });
      showSuccessToast("Free item selected successfully");
    } else {
      setSelectedFreeItem(null);
      form.setFieldsValue({ FreeItemID: null });
    }
    closeAllModals();
  };

  const handleDiscountItemsSelect = (selected) => {
    const list = Array.isArray(selected) ? selected : [];
    const ids = [];
    const labels = new Map();
    list.forEach((p) => {
      const id =
        typeof p === "object"
          ? p.menuItem_ID ?? p.MenuItem_ID ?? p.id
          : p;
      if (id == null) return;
      ids.push(id);
      labels.set(
        String(id),
        typeof p === "object"
          ? p.menuItem_Name || p.MenuItem_Name || p.name || `Item ${id}`
          : `Item ${id}`
      );
    });
    setSelectedItems(ids);
    setSelectedItemLabels(labels);
    closeAllModals();
  };

  useEffect(() => {
    return () => {
      document.body.style.overflow = "unset";
      document.body.classList.remove("modal-open");
    };
  }, []);

  const handleRewardTypeChange = (value) => {
    setRewardType(value);
    setSelectedItems([]);
    setSelectedCategories([]);
    setSelectedFreeItem(null);
    form.setFieldsValue({
      RewardType: value,
      DiscountType: null,
      Amount: null,
      FreeItemID: null,
    });
  };

  const handleStatusToggle = (checked) => {
    setStatusEnabled(checked);
    form.setFieldsValue({ Status: checked });
  };

  const initialFormSnapshotRef = useRef(null);

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      rewardType: rewardType || null,
      statusEnabled: Boolean(statusEnabled),
      RewardName: watchedRewardName || "",
      DiscountType: watchedDiscountType || null,
      Amount: watchedAmount ?? null,
      RequiredPoints: watchedRequiredPoints ?? null,
      selectedItems,
      selectedCategories,
      freeItemId:
        selectedFreeItem?.MenuItem_ID ||
        selectedFreeItem?.menuItem_ID ||
        selectedFreeItem?.id ||
        null,
    });
  }, [
    rewardType,
    statusEnabled,
    watchedRewardName,
    watchedDiscountType,
    watchedAmount,
    watchedRequiredPoints,
    selectedItems,
    selectedCategories,
    selectedFreeItem,
  ]);

  useEffect(() => {
    if (initialFormSnapshotRef.current === null) {
      initialFormSnapshotRef.current = getFormSnapshot();
    }
  }, [getFormSnapshot]);

  const isDirty =
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return false;
    }

    try {
      setSaving(true);

      if (!hasLoyaltyRule) {
        showErrorToast(
          "A loyalty rule must be set up before creating rewards."
        );
        navigate("/loyalty/rules");
        return false;
      }

      if (values.RewardType === "DISCOUNT_ON_ITEMS" && selectedItems.length === 0) {
        showErrorToast("Please select at least one item for the loyalty rule");
        return false;
      }
      if (
        values.RewardType === "DISCOUNT_ON_CATEGORY" &&
        selectedCategories.length === 0
      ) {
        showErrorToast("Please select at least one category for the loyalty rule");
        return false;
      }
      if (values.RewardType === "FREE_ITEM" && !selectedFreeItem) {
        showErrorToast("Please select a free item for this reward");
        return false;
      }

      let conditions = [];
      if (values.RewardType === "DISCOUNT_ON_ITEMS") {
        conditions = selectedItems.map((id) => ({ ItemORCategoryID: id }));
      } else if (values.RewardType === "DISCOUNT_ON_CATEGORY") {
        conditions = selectedCategories.map((id) => ({ ItemORCategoryID: id }));
      }

      let freeItemID = null;
      if (values.RewardType === "FREE_ITEM") {
        if (selectedFreeItem) {
          freeItemID =
            selectedFreeItem.MenuItem_ID ||
            selectedFreeItem.menuItem_ID ||
            selectedFreeItem.id;
        } else if (values.FreeItemID) {
          freeItemID = values.FreeItemID;
        }
      }

      const payload = {
        CompID: CompID,
        RewardName: values.RewardName,
        RewardType: values.RewardType,
        DiscountType: values.DiscountType || null,
        Amount: values.Amount || null,
        RequiredPoints: values.RequiredPoints,
        FreeItemID: freeItemID !== undefined ? freeItemID : null,
        Status: values.Status !== undefined ? values.Status : true,
        Conditions: conditions,
      };

      const response = await axiosInstance.post(`${APIs.UPSERT_REWARD}`, payload);

      if (response.data.status) {
        showSuccessToast("Reward created successfully");
        initialFormSnapshotRef.current = getFormSnapshot();
        if (exitAfterSave) {
          navigate("/loyalty/rewards");
        }
        return true;
      } else {
        showErrorToast(response.data.message || "Failed to create reward");
        return false;
      }
    } catch (error) {
      console.error("Error creating reward:", error);
      showErrorToast("Error creating reward");
      return false;
    } finally {
      setSaving(false);
    }
  };

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
  });

  const selectedTypeMeta = REWARD_TYPES.find((t) => t.value === rewardType);

  const getPreviewText = () => {
    if (!rewardType) return "Select a reward type to see how redemption will work.";
    if (rewardType === "DISCOUNT_ON_SALE") {
      if (!watchedDiscountType || !watchedAmount) {
        return "Choose discount type and amount to preview the sale discount.";
      }
      const amountLabel =
        watchedDiscountType === "PERCENTAGE"
          ? `${watchedAmount}% off`
          : `$${watchedAmount} off`;
      return `Customers redeem points for ${amountLabel} their entire order.`;
    }
    if (rewardType === "FREE_ITEM") {
      if (!selectedFreeItem) return "Select which menu item customers receive for free.";
      const name =
        selectedFreeItem.MenuItem_Name || selectedFreeItem.menuItem_Name || "item";
      return `Customers redeem points for a free ${name}.`;
    }
    if (rewardType === "DISCOUNT_ON_ITEMS") {
      return "Customer gets discount on selected items.";
    }
    if (rewardType === "DISCOUNT_ON_CATEGORY") {
      return "Customer gets discount on selected categories.";
    }
    return "";
  };

  const renderBreadcrumb = () => (
    <div className="d-flex align-items-center mb-3">
      <div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="#/">Home</a>
          </li>
          <li className="breadcrumb-item">
            <Link to="/loyalty/rewards">Rewards Management</Link>
          </li>
          <li className="breadcrumb-item active">Create Reward</li>
        </ol>
        <h1 className="page-header mb-0">Create New Reward</h1>
        <p className="text-muted">Set up a new loyalty reward for your customers</p>
      </div>
      <SaveActions
        onCancel={() => navigate("/loyalty/rewards")}
        onSave={() => handleSubmit({ exitAfterSave: false })}
        onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
        saving={saving}
      />
    </div>
  );

  const renderFreeItemPicker = () => (
    <Form.Item
      name="FreeItemID"
      label="Free item"
      rules={[{ required: true, message: "Please select a free item" }]}
      extra="Customers receive this item when they redeem the reward."
    >
      <div className={`reward-free-item-box ${isDarkMode ? "reward-free-item-box--dark" : ""}`}>
        {selectedFreeItem ? (
          <div className="reward-free-item-selected">
            <div className="reward-free-item-selected__left">
              <div className="reward-free-item-thumb">
                {selectedFreeItem.menuItem_Image || selectedFreeItem.MenuItem_Image ? (
                  <img
                    src={
                      selectedFreeItem.menuItem_Image || selectedFreeItem.MenuItem_Image
                    }
                    alt={
                      selectedFreeItem.MenuItem_Name || selectedFreeItem.menuItem_Name
                    }
                  />
                ) : (
                  <i className="fa fa-box" aria-hidden="true" />
                )}
              </div>
              <div>
                <div className="reward-free-item-name">
                  {selectedFreeItem.MenuItem_Name || selectedFreeItem.menuItem_Name}
                </div>
                {(selectedFreeItem.menuItem_SalePrice ||
                  selectedFreeItem.MenuItem_SalePrice) && (
                  <div className="reward-free-item-price">
                    $
                    {parseFloat(
                      selectedFreeItem.menuItem_SalePrice ||
                        selectedFreeItem.MenuItem_SalePrice ||
                        0
                    ).toFixed(2)}
                  </div>
                )}
              </div>
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="reward-secondary-btn"
                style={{ "--reward-accent": themeColor }}
                onClick={() => openModal("showFreeItemModal")}
              >
                Change
              </button>
              <button
                type="button"
                className="reward-remove-btn"
                onClick={() => {
                  setSelectedFreeItem(null);
                  form.setFieldsValue({ FreeItemID: null });
                }}
                aria-label="Remove free item"
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            </div>
          </div>
        ) : (
          <div className="reward-free-item-empty">
            <GiftOutlined />
            <p>No free item selected yet.</p>
            <button
              type="button"
              className="btn px-4"
              onClick={() => openModal("showFreeItemModal")}
              style={{
                backgroundColor: themeColor,
                border: "none",
                color: "white",
                borderRadius: "8px",
                fontWeight: 600,
              }}
            >
              <i className="fa fa-plus me-2" aria-hidden="true" />
              Select Free Item
            </button>
          </div>
        )}
      </div>
    </Form.Item>
  );

  if (checkingRule) {
    return (
      <div
        className={`create-reward-page ${isDarkMode ? "create-reward-page--dark" : ""}`}
        style={{ "--reward-accent": themeColor }}
      >
        {renderBreadcrumb()}
        <div className="text-muted py-5 text-center">
          Checking loyalty rule setup...
        </div>
      </div>
    );
  }

  if (!hasLoyaltyRule) {
    return null;
  }

  return (
    <div
      className={`create-reward-page ${isDarkMode ? "create-reward-page--dark" : ""}`}
      style={{ "--reward-accent": themeColor }}
    >
      {renderBreadcrumb()}

      <Form
        form={form}
        layout="vertical"
        onFinish={() => handleSubmit({ exitAfterSave: true })}
        initialValues={{ Status: true }}
      >
        <div
          className={`reward-config-shell ${isDarkMode ? "reward-config-shell--dark" : ""}`}
        >
          <div className="reward-config-intro">
            <h5 className="reward-config-intro__title">
              Set up how customers redeem points
            </h5>
            <p className="reward-config-intro__text mb-0">
              Choose a reward type, configure the offer details, then activate it when
              you are ready.
            </p>
          </div>

          <div className="reward-config-section">
            <div className="reward-config-section__header">
              <span className="reward-config-step">1</span>
              <div>
                <h6 className="mb-0">Choose reward type</h6>
                <small className="text-muted">
                  Pick how customers will spend their loyalty points
                </small>
              </div>
            </div>

            <div
              className={`reward-type-grid ${isDarkMode ? "reward-type-grid--dark" : ""}`}
              role="radiogroup"
              aria-label="Reward type"
            >
              {REWARD_TYPES.map(({ value, title, description, Icon }) => {
                const isSelected = rewardType === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    className={`reward-type-card ${
                      isSelected ? "reward-type-card--selected" : ""
                    }`}
                    onClick={() => handleRewardTypeChange(value)}
                  >
                    <span className="reward-type-card__icon">
                      <Icon />
                    </span>
                    <span className="reward-type-card__body">
                      <span className="reward-type-card__title">{title}</span>
                      <span className="reward-type-card__desc">{description}</span>
                    </span>
                    {isSelected && (
                      <CheckCircleOutlined className="reward-type-card__check" />
                    )}
                  </button>
                );
              })}
            </div>
            <Form.Item
              name="RewardType"
              rules={[{ required: true, message: "Please select a reward type" }]}
              className="mb-0 reward-type-hidden-field"
            >
              <Select aria-hidden="true" tabIndex={-1}>
                {REWARD_TYPES.map(({ value, title }) => (
                  <Option key={value} value={value}>
                    {title}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="row g-4">
            <div className="col-lg-8">
              <div
                className={`reward-config-main ${
                  isDarkMode ? "reward-config-main--dark" : ""
                }`}
              >
                <div className="reward-config-section__header mb-3">
                  <span className="reward-config-step">2</span>
                  <div>
                    <h6 className="mb-0">Configure reward</h6>
                    <small className="text-muted">
                      {rewardType
                        ? selectedTypeMeta?.description
                        : "Select a reward type above to continue"}
                    </small>
                  </div>
                </div>

                {!rewardType && (
                  <div className="reward-config-awaiting">
                    <GiftOutlined />
                    <div>
                      <strong>Waiting for reward type</strong>
                      <p className="mb-0">
                        Once you choose a reward type, the configuration fields will
                        appear here.
                      </p>
                    </div>
                  </div>
                )}

                {rewardType && (
                  <>
                    <Form.Item
                      name="RewardName"
                      label="Reward name"
                      rules={[{ required: true, message: "Please enter reward name" }]}
                      extra="Shown to staff and customers when redeeming."
                    >
                      <Input
                        placeholder="e.g., $5 Off Entire Sale"
                        size="large"
                        className="reward-config-input"
                      />
                    </Form.Item>

                    {(rewardType === "DISCOUNT_ON_SALE" ||
                      rewardType === "DISCOUNT_ON_ITEMS" ||
                      rewardType === "DISCOUNT_ON_CATEGORY") && (
                      <>
                        <Form.Item
                          name="DiscountType"
                          label="Discount type"
                          rules={[
                            { required: true, message: "Please select discount type" },
                          ]}
                        >
                          <Select
                            placeholder="Select discount type"
                            size="large"
                            getPopupContainer={(trigger) => trigger.parentNode}
                            dropdownStyle={{ zIndex: 1050 }}
                          >
                            <Option value="FIXED_AMOUNT">Fixed Amount ($)</Option>
                            <Option value="PERCENTAGE">Percentage (%)</Option>
                          </Select>
                        </Form.Item>

                        <Form.Item
                          name="Amount"
                          label="Discount amount"
                          rules={[
                            { required: true, message: "Please enter discount amount" },
                          ]}
                        >
                          <InputNumber
                            min={0.01}
                            step={0.01}
                            placeholder="5.00"
                            size="large"
                            className="w-100 reward-config-input"
                          />
                        </Form.Item>

                        {rewardType === "DISCOUNT_ON_ITEMS" && (
                          <Form.Item label="Select items">
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => openModal("showItemModal")}
                              style={{
                                backgroundColor: themeColor,
                                border: `1px solid ${themeColor}`,
                                color: "#fff",
                                marginBottom: 8,
                              }}
                            >
                              Select items
                            </button>
                            {selectedItems.length > 0 && (
                              <div className="d-flex flex-wrap gap-2">
                                {selectedItems.map((id) => (
                                  <span
                                    key={id}
                                    className="badge"
                                    style={{
                                      background: isDarkMode ? "#4a5568" : "#e9ecef",
                                      color: isDarkMode ? "#e2e8f0" : "#495057",
                                      padding: "6px 10px",
                                    }}
                                  >
                                    {selectedItemLabels.get(String(id)) || `Item ${id}`}
                                    <button
                                      type="button"
                                      className="btn btn-link btn-sm p-0 ms-2"
                                      style={{ color: "inherit", textDecoration: "none" }}
                                      onClick={() =>
                                        setSelectedItems((prev) =>
                                          prev.filter((x) => String(x) !== String(id))
                                        )
                                      }
                                    >
                                      ×
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}
                          </Form.Item>
                        )}

                        {rewardType === "DISCOUNT_ON_CATEGORY" && (
                          <Form.Item label="Select categories">
                            <Select
                              mode="multiple"
                              placeholder="Select categories for discount"
                              value={selectedCategories}
                              onChange={setSelectedCategories}
                              style={{ width: "100%" }}
                              getPopupContainer={(trigger) => trigger.parentNode}
                              dropdownStyle={{ zIndex: 1050 }}
                            >
                              {categories.map((cat) => (
                                <Option key={cat.RootCat_ID} value={cat.RootCat_ID}>
                                  {cat.RootCat_Name}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        )}
                      </>
                    )}

                    {rewardType === "FREE_ITEM" && renderFreeItemPicker()}

                    <Form.Item
                      name="RequiredPoints"
                      label="Points required"
                      rules={[
                        { required: true, message: "Please enter points required" },
                      ]}
                      extra="Loyalty points a customer must spend to redeem this reward."
                    >
                      <InputNumber
                        min={1}
                        placeholder="100"
                        size="large"
                        className="w-100 reward-config-input"
                      />
                    </Form.Item>
                  </>
                )}
              </div>
            </div>

            <div className="col-lg-4">
              <div
                className={`reward-status-panel ${
                  isDarkMode ? "reward-status-panel--dark" : ""
                }`}
              >
                <div className="reward-status-panel__header">
                  <CheckCircleOutlined />
                  <div>
                    <h5 className="mb-0">Status &amp; preview</h5>
                    <small>Control whether this reward is available</small>
                  </div>
                </div>

                <Form.Item
                  name="Status"
                  label="Reward status"
                  valuePropName="checked"
                  className="mb-3"
                >
                  <ToggleSwitch
                    isChecked={statusEnabled}
                    onToggle={handleStatusToggle}
                    label={
                      statusEnabled ? (
                        <span className="text-success">
                          <CheckCircleOutlined className="me-1" />
                          Active
                        </span>
                      ) : (
                        <span className="text-muted">
                          <StopOutlined className="me-1" />
                          Inactive
                        </span>
                      )
                    }
                    name="Status"
                  />
                </Form.Item>

                <div className="reward-status-preview">
                  <div className="reward-status-preview__label">
                    <InfoCircleOutlined className="me-1" />
                    How it works
                  </div>
                  <p className="reward-status-preview__text mb-2">{getPreviewText()}</p>
                  <div className="reward-status-preview__meta">
                    {rewardType && (
                      <span className="reward-status-chip">
                        {selectedTypeMeta?.title || rewardType}
                      </span>
                    )}
                    {watchedRequiredPoints ? (
                      <span className="reward-status-chip reward-status-chip--muted">
                        {watchedRequiredPoints} pts
                      </span>
                    ) : null}
                    {watchedRewardName ? (
                      <span className="reward-status-chip reward-status-chip--muted">
                        {watchedRewardName}
                      </span>
                    ) : null}
                    {!statusEnabled && (
                      <span className="reward-status-chip reward-status-chip--warn">
                        Not available yet
                      </span>
                    )}
                  </div>
                </div>

                {!rewardType && (
                  <div className="reward-status-hint">
                    Tip: Sale Discount is best for simple order-wide offers.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Form>

      <ProductMultiSelectionModal
        selectedProducts={selectedItems}
        setSelectedProducts={handleDiscountItemsSelect}
        showModal={modals.showItemModal}
        setShowModal={(val) =>
          val ? openModal("showItemModal") : closeAllModals()
        }
        onSubmit={handleDiscountItemsSelect}
        setLoading={setSelectorLoading}
        showButton={true}
      />

      <ProductMultiSelectionModal
        selectedProducts={
          selectedFreeItem
            ? [selectedFreeItem.MenuItem_ID || selectedFreeItem.menuItem_ID]
            : []
        }
        setSelectedProducts={handleFreeItemSelect}
        showModal={modals.showFreeItemModal}
        setShowModal={(val) =>
          val ? openModal("showFreeItemModal") : closeAllModals()
        }
        onSubmit={handleFreeItemSelect}
        setLoading={setSelectorLoading}
        showButton={true}
        maxSelection={1}
      />
    </div>
  );
};

export default CreateReward;
